import { Alert, Button, Card, Form, Input, Select, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type Template = { id: string; template_code: string; name: string; channel: string };
type FormValues = {
  template_id: string;
  student_ids: string[];
  body_override?: string;
  summary?: string;
};

export default function CommsSendFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.comms.manage') || user?.permissions?.includes('education.admin')
  );

  const templates = useQuery({
    queryKey: ['education-comm-templates'],
    queryFn: async () => {
      const res = await api.get<Template[]>('/education/comms/templates', { params: { status: 'active' } });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post<{ id: string }>('/education/comms/sends', values),
    onSuccess: (res) => {
      message.success(t('commSendCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/comms/sends/${id}` : '/education/comms/sends');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newCommSend')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newCommSend')}
      description={t('commSendsIntro')}
      extra={<Button onClick={() => navigate('/education/comms/sends')}>{t('commSends')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 720 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('commsOutboundHint')} />
        <Form layout="vertical" onFinish={(values) => create.mutate(values)}>
          <Form.Item name="template_id" label={t('template')} rules={[{ required: true }]}>
            <Select
              loading={templates.isLoading}
              options={(templates.data || []).map((tpl) => ({
                value: tpl.id,
                label: `${tpl.template_code} — ${tpl.name} (${tpl.channel})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="student_ids" label={t('students')} rules={[{ required: true }]}>
            <EducationStudentSelect mode="multiple" />
          </Form.Item>
          <Form.Item name="body_override" label={t('messageOverride')} extra={t('messageOverrideHint')}>
            <Input.TextArea rows={4} maxLength={4000} />
          </Form.Item>
          <Form.Item name="summary" label={t('summary')}>
            <Input maxLength={1000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
